// frontend/src/pages/Login.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useLanguageStore } from '../store/useLanguageStore';
import { LanguageSelector } from '../components/LanguageSelector';
import { Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Modal } from '../components/Modal';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// ══════════════════════════════════════════════════════════════════════
// COMPONENTE INTERNO (Consume el hook useGoogleReCaptcha)
// ══════════════════════════════════════════════════════════════════════
const LoginForm = ({ setUser }) => {
  const { t, language, setLanguage } = useLanguageStore();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState('');

  // Estados del Modal de Recuperación
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);
  const [forgotSuccessEmail, setForgotSuccessEmail] = useState('');

  // ── Detectar idioma del query param ?lang=pt ──────────────────────
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const langParam = searchParams.get('lang')?.toLowerCase();
    if (langParam && ['es', 'en', 'pt'].includes(langParam) && langParam !== language) {
      setLanguage(langParam);
    }
  }, []);

  const loginSchema = useMemo(() => z.object({
    email: z.string().email(t('login.errors.invalid_email_format') || 'Correo inválido'),
    password: z.string().min(1, t('common.validation_error') || 'Contraseña obligatoria')
  }), [t, language]);

  const forgotSchema = useMemo(() => z.object({
    email: z.string().email(t('login.errors.invalid_email_format') || 'Correo inválido')
  }), [t, language]);

  const {
    register: registerLogin,
    handleSubmit: handleSubmitLogin,
    formState: { errors: errorsLogin }
  } = useForm({ resolver: zodResolver(loginSchema) });

  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    reset: resetForgot,
    formState: { errors: errorsForgot }
  } = useForm({ resolver: zodResolver(forgotSchema) });

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    resetForgot();
    setForgotSuccessEmail('');
    setGlobalError('');
  };

  // ── LOGIN con reCAPTCHA v3 ────────────────────────────────────────
  const onLoginSubmit = async (data) => {
    setIsSubmitting(true);
    setGlobalError('');

    try {
      // 🔒 Obtener token reCAPTCHA v3 (invisible)
      let recaptchaToken = '';
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('login');
      }

      const params = new URLSearchParams();
      params.append('username', data.email);
      params.append('password', data.password);

      const response = await apiClient.post(`/auth/login`, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Recaptcha-Token': recaptchaToken,   // ← header para el backend
        },
        withCredentials: true
      });

      localStorage.setItem("vnext_auth_token", response.data.access_token);
      localStorage.setItem("vnext_user", JSON.stringify(response.data.user));

      if (setUser) setUser(response.data.user);
      apiClient.post('/ai-mapping/warmup').catch(() => { });
      navigate("/dashboard");

    } catch (err) {
      setGlobalError(err.message || t('common.error_server'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── FORGOT PASSWORD con reCAPTCHA v3 ──────────────────────────────
  const onForgotSubmit = async (data) => {
    setIsForgotSubmitting(true);
    try {
      // 🔒 Obtener token reCAPTCHA v3 (invisible)
      let recaptchaToken = '';
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('forgot_password');
      }

      await apiClient.post(`/auth/forgot-password`, {
        email: data.email,
        recaptcha_token: recaptchaToken,   // ← campo en body JSON
      });
      setForgotSuccessEmail(data.email);
    } catch (err) {
      // Siempre muestra éxito por seguridad contra enumeración de usuarios
      setForgotSuccessEmail(data.email);
    } finally {
      setIsForgotSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-2 sm:p-4">
      <div className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-xl max-w-sm sm:max-w-md w-full relative animate-in fade-in duration-500">

        <div className="absolute top-4 right-4 sm:top-6 sm:right-8">
          <LanguageSelector />
        </div>

        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800">{t('login.title')}</h2>
          <p className="text-slate-500 mt-2 text-xs sm:text-sm">{t('login.subtitle')}</p>
        </div>

        {globalError && (
          <div className="bg-red-50 text-red-600 p-3 sm:p-4 rounded-xl text-xs sm:text-sm text-center font-medium mb-6 animate-in fade-in zoom-in duration-200">
            {globalError}
          </div>
        )}

        <form onSubmit={handleSubmitLogin(onLoginSubmit)} className="space-y-4 sm:space-y-5" noValidate>
          <div>
            <label className="block text-[10px] sm:text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 sm:mb-2">
              {t('login.email_label')}
            </label>
            <div className="relative">
              <input
                type="email"
                {...registerLogin('email')}
                className={`w-full bg-slate-50 border-2 rounded-xl sm:rounded-2xl py-2.5 sm:py-3 pl-10 sm:pl-11 pr-4 outline-none transition-all text-xs sm:text-sm font-medium ${errorsLogin.email ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'
                  }`}
                placeholder={t('common.placeholders.email')}
              />
              <Mail className="absolute left-3.5 top-[11px] sm:left-4 sm:top-[14px] text-slate-400 w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            {errorsLogin.email && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{errorsLogin.email.message}</span>}
          </div>

          <div>
            <div className="flex justify-between items-center ml-1 mb-1 sm:mb-2 gap-2">
              <label className="text-[10px] sm:text-[11px] font-black text-slate-400 uppercase tracking-widest truncate">
                {t('login.pass_label')}
              </label>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="text-[11px] sm:text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors whitespace-nowrap"
              >
                {t('login.forgot_pass')}
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                {...registerLogin('password')}
                className={`w-full bg-slate-50 border-2 rounded-xl sm:rounded-2xl py-2.5 sm:py-3 pl-10 sm:pl-11 pr-11 sm:pr-12 outline-none text-xs sm:text-sm font-medium transition-all ${errorsLogin.password ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'
                  }`}
                placeholder="••••••••"
              />
              <Lock className="absolute left-3.5 top-[11px] sm:left-4 sm:top-[14px] text-slate-400 w-4 h-4 sm:w-5 sm:h-5" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-[11px] sm:right-4 sm:top-[14px] text-slate-400 hover:text-blue-600 transition-colors"
              >
                {showPassword ? <EyeOff size={16} className="sm:size-[18px]" /> : <Eye size={16} className="sm:size-[18px]" />}
              </button>
            </div>
            {errorsLogin.password && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{errorsLogin.password.message}</span>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black mt-3 sm:mt-4 shadow-lg shadow-blue-200 hover:bg-blue-700 hover:shadow-xl transition-all flex justify-center items-center gap-2 disabled:bg-slate-400 disabled:shadow-none text-sm sm:text-base"
          >
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t('login.button')}
          </button>
        </form>

        <p className="text-center text-xs sm:text-sm font-medium text-slate-500 mt-6 sm:mt-8">
          {t('login.no_account')}{' '}
          <Link to="/register" className="text-blue-600 font-bold hover:underline">
            {t('login.register_link')}
          </Link>
        </p>
      </div>

      {/* MODAL RECUPERAR CONTRASEÑA */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative animate-in zoom-in-95 duration-200">
            <h3 className="text-xl sm:text-2xl font-black text-slate-800 mb-2 truncate">
              {t('login.forgot_modal_title') || 'Recuperar contraseña'}
            </h3>

            {forgotSuccessEmail ? (
              <div className="bg-green-50 text-green-800 p-4 sm:p-5 rounded-xl sm:rounded-2xl text-xs sm:text-sm leading-relaxed border border-green-100 mt-5 sm:mt-6">
                {t('login.forgot_success_msg_1') || 'Si el correo'} <b className="font-black break-all">{forgotSuccessEmail}</b> {t('login.forgot_success_msg_2') || 'está registrado, recibirá instrucciones en breve.'}
                <br /><br />
                {t('login.forgot_spam_note') || 'Revisa tu carpeta de "spam" si no lo ves en unos minutos.'}
                <button
                  onClick={closeModal}
                  className="w-full bg-slate-800 text-white py-2.5 sm:py-3 rounded-xl font-bold mt-5 sm:mt-6 hover:bg-slate-900 transition-colors text-sm"
                >
                  {t('common.buttons.understand') || 'Entendido'}
                </button>
              </div>
            ) : (
              <>
                <p className="text-slate-500 text-xs sm:text-sm mb-5 sm:mb-6">
                  {t('login.forgot_modal_subtitle') || 'Te enviaremos un enlace para restablecer tu acceso.'}
                </p>
                <form onSubmit={handleSubmitForgot(onForgotSubmit)} noValidate>
                  <div className="mb-5 sm:mb-6">
                    <label className="block text-[10px] sm:text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 sm:mb-2">
                      {t('login.email_label')}
                    </label>
                    <input
                      type="email"
                      {...registerForgot('email')}
                      className={`w-full bg-slate-50 border-2 rounded-xl sm:rounded-2xl py-2.5 sm:py-3 px-4 outline-none text-xs sm:text-sm font-medium ${errorsForgot.email ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'
                        }`}
                      placeholder={t('common.placeholders.email')}
                    />
                    {errorsForgot.email && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{errorsForgot.email.message}</span>}
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 bg-slate-100 text-slate-600 py-2.5 sm:py-3 rounded-xl font-bold hover:bg-slate-200 transition-colors text-sm"
                    >
                      {t('common.buttons.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={isForgotSubmitting}
                      className="flex-1 bg-blue-600 text-white py-2.5 sm:py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors flex justify-center items-center disabled:bg-blue-400 text-sm"
                    >
                      {isForgotSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('login.forgot_submit_btn') || 'Enviar correo'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════════════
// EXPORT: Wrapper con GoogleReCaptchaProvider (mismo patrón que Register.jsx)
// ══════════════════════════════════════════════════════════════════════
export const Login = ({ setUser }) => {
  const { language } = useLanguageStore();

  if (!RECAPTCHA_KEY) {
    // DEV sin key: renderizar sin provider (captcha.py hace skip en backend también)
    return <LoginForm setUser={setUser} />;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHA_KEY}
      language={language}
    >
      <LoginForm setUser={setUser} />
    </GoogleReCaptchaProvider>
  );
};
